/* global G */
import { setData } from 'lib/sequence/model/api/set'
import sequenceModelSubmit from 'lib/sequence/model/api/submit'
import seqReset from 'lib/sequence/model/api/reset'
import { actionWithHooks } from 'lib/sequence/module/action'
import { BLE_PERIPHERAL_CONNECTION_STATUS } from 'model/_shared/collection/bluetooth/constants'
import showToast from 'app/_shared/component/showToast'
import { showBlockingDialog } from 'app/_shared/events/dialog'
import { getMaxSeenDate } from 'app/xEcoApm/helper'
import { INIT_LIC } from 'app/xEcoApm/helper/apm'
import { persistSensor } from 'app/xEcoApm/helper/offline'
import { isDate } from 'lib/util/date'

/**
 * Shows a toast informing the user that a sensor's license could be successfully activated.
 *
 * @param module  current module
 * @param nick    sensor's nick property
 */
const showLicenceActivatedToast = async (module: Gaia.AppModule.Spec, nick: string) => await showToast(module, {
  message: {
    ns: 'apmGo',
    key: 'toast.licence.activated.message',
    defaultValue: `Sensor {{nick}} license has been activated`,
    nick,
  },
  severity: 'info',
  duration: 3000,
  close: true,
})

/**
 * Shows a dialog informing the user that a sensor's license could not be activated.
 *
 * @param module  current module
 * @param nick    sensor's nick property
 */
const showLicenceErrorDialog = async (module: Gaia.AppModule.Spec, nick: string) => await showBlockingDialog(module, null, {
  title: {
    ns: 'apmGo',
    key: 'dialog.licenceActivationError.title',
    defaultValue: 'Error',
    nick,
  },
  text: {
    ns: 'apmGo',
    key: 'dialog.licenceActivationError.text',
    defaultValue: `Sensor {{nick}} license could not be activated`,
    nick,
  },
})

/**
 * If the current `sensor` is connected and its `licensedUntil` date is not valid, attempts to
 * activate its license.
 *
 * @param obj current module
 */
const licenceActivate = (obj: Gaia.AppModule.Spec) => async ( ...args: any[])  => {
  const online = obj[G.ADAPTER][G.SESSION][G.STATE][G.ONLINE]
  const actionState = obj[G.STATE][G.ACTION][G.STATE]
  const bluetoothAdapter = obj[G.ADAPTER][G.BLUETOOTH]
  const { sensor, pairing } = obj[G.MODEL][G.CHILDREN]
  const { value: { name, nick, licensedUntil } } = sensor[G.CACHE]
  const peripheral = bluetoothAdapter[G.GROUP][name]
  const { connectionStatus } = peripheral?.[G.STATE] || {}
  const { attemptedLicenseActivation } = actionState

  const connected = connectionStatus === BLE_PERIPHERAL_CONNECTION_STATUS.CONNECTED

  if (connected && !attemptedLicenseActivation && !isDate(licensedUntil)) {
    actionState.attemptedLicenseActivation = true

    try {
      const date = getMaxSeenDate(obj)
      const response = await peripheral[G.API].send(INIT_LIC({ date }))

      if (response.e) {
        setData(pairing, {
          ...sensor[G.CACHE].value,
          licensedUntil: response.e,
        }, { updateCache: true })

        if (online) {
          await sequenceModelSubmit(pairing)
        } else {
          await persistSensor(obj)
        }

        seqReset(sensor)

        // status (s) 4 can mean that the license had already been
        // activated, therefore, we keep the app silent in that case
        // and only show an error if the device returned no date (e)
        if (response.s === 0) {
          await showLicenceActivatedToast(obj, nick)
        }
        await actionWithHooks(obj[G.STATE][G.ACTION])([])
      } else {
        await showLicenceErrorDialog(obj, nick)
      }
    } catch (e) {
      await showLicenceErrorDialog(obj, nick)
      console.warn(e)
    }
  }

  return args
}

export default licenceActivate
